import React, { useMemo } from 'react';

function sevBucket(sev) {
  const n = Number(sev || 0);
  if (n >= 0.75) return 'critical';
  if (n >= 0.5) return 'storm';
  if (n >= 0.25) return 'elevated';
  return 'nominal';
}

function ageShort(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t || isNaN(t)) return '—';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

export default function RiskWatchlistTable({ insights, selectedInsightId, selectedSector, onSelect }) {
  const rows = useMemo(() => {
    let list = insights || [];
    if (selectedSector) {
      list = list.filter((i) => (i.domain || i.sector || '').toLowerCase() === selectedSector);
    }
    return [...list].sort((a, b) => (b.severity || 0) - (a.severity || 0));
  }, [insights, selectedSector]);

  if (rows.length === 0) {
    return <div className="atl-empty" data-testid="risk-table-empty">No active issues{selectedSector ? ` in ${selectedSector}` : ''}</div>;
  }

  return (
    <table className="risk-table" data-testid="risk-table">
      <thead>
        <tr>
          <th className="col-sev">Sev</th>
          <th>Sector</th>
          <th>Region</th>
          <th>Issue</th>
          <th className="col-conf">Conf</th>
          <th className="col-impact">Impact</th>
          <th className="col-age">Age</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((i) => {
          const bucket = sevBucket(i.severity);
          const conf = Math.round((i.confidence || 0) * 100);
          const impact = Math.min(5, Math.max(0, Math.round((i.impact || i.severity || 0) * 5)));
          return (
            <tr
              key={i.id}
              className={selectedInsightId === i.id ? 'is-active' : ''}
              onClick={() => onSelect && onSelect(i.id)}
              data-testid={`risk-row-${i.id}`}
            >
              <td className="col-sev"><span className={`sev-pill ${bucket}`}>{bucket}</span></td>
              <td style={{ color: 'var(--studio-text-2)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>{(i.domain || i.sector || '—').replace(/_/g, ' ')}</td>
              <td style={{ color: 'var(--studio-text-1)' }}>{i.region || i.scope || '—'}</td>
              <td style={{ color: 'var(--studio-text-0)' }}>{i.title || i.headline || i.summary?.slice(0, 80) || '—'}</td>
              <td className="col-conf">
                <div className="conf-cell">
                  <span>{conf}</span>
                  <div className="studio-bar"><i style={{ width: `${conf}%` }} /></div>
                </div>
              </td>
              <td className="col-impact">
                <span className="impact-dots" aria-label={`impact ${impact}/5`}>
                  {[0, 1, 2, 3, 4].map((k) => <i key={k} className={k < impact ? 'on' : ''} />)}
                </span>
              </td>
              <td className="col-age">{ageShort(i.updated_at || i.observed_at || i.timestamp)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
